// Outlaw's Loot — audio manager. Wires the approved event names to the
// provided SILENT placeholder WAVs. No autoplay before user interaction;
// respects mute/volume. Replacing a placeholder file later changes nothing here.
import { AUDIO_SRC } from "./assets";

class OutlawsAudio {
  constructor() {
    this.buffers = {};
    this.unlocked = false;
    this.muted = false;
    this.volume = 0.7;
    this.ambient = null;
  }

  preload() {
    Object.entries(AUDIO_SRC).forEach(([name, src]) => {
      if (this.buffers[name]) return;
      const a = new Audio(src);
      a.preload = "auto";
      a.volume = this.volume;
      this.buffers[name] = a;
    });
  }

  unlock() {
    // Call from the first user gesture so browsers permit later playback.
    if (this.unlocked) return;
    this.unlocked = true;
    this.preload();
  }

  setMuted(m) {
    this.muted = m;
    if (this.ambient) this.ambient.muted = m;
  }

  play(name) {
    if (!this.unlocked || this.muted) return;
    // Owner (2026-06): reel sounds removed — no spin-start whoosh / stop clicks.
    if (name === "reel_spin_start" || name === "reel_stop") return;
    const base = this.buffers[name];
    if (!base) return;
    try {
      const inst = base.cloneNode();
      inst.volume = this.volume;
      const p = inst.play();
      if (p && p.catch) p.catch(() => {});
    } catch (_e) { /* ignore */ }
  }

  startAmbient() {
    if (!this.unlocked || this.muted) return;
    if (this.ambient) return;
    const a = this.buffers.ambient_loop;
    if (!a) return;
    a.loop = true;
    a.volume = this.volume * 0.5;
    this.ambient = a;
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
  }

  _ambientTarget() {
    return this.volume * 0.5;
  }

  _clearAmbientFade() {
    if (this._ambientFade) { clearInterval(this._ambientFade); this._ambientFade = null; }
  }

  // Smoothly fade the saloon background out over ~0.5s, then pause — so the
  // bonus music takes over cleanly instead of a hard cut.
  pauseAmbient(ms = 500) {
    const a = this.ambient;
    if (!a) return;
    this._clearAmbientFade();
    const target = this._ambientTarget();
    const steps = 20;
    const stepMs = Math.max(10, ms / steps);
    let i = 0;
    this._ambientFade = setInterval(() => {
      i += 1;
      const v = target * (1 - i / steps);
      try { a.volume = Math.max(0, v); } catch (_e) { /* ignore */ }
      if (i >= steps) {
        this._clearAmbientFade();
        try { a.pause(); a.volume = target; } catch (_e) { /* ignore */ }
      }
    }, stepMs);
  }

  // Fade the saloon background back in over ~0.5s after the bonus ends.
  resumeAmbient(ms = 500) {
    if (this.muted) return;
    this._clearAmbientFade();
    if (!this.ambient) { this.startAmbient(); return; }
    const a = this.ambient;
    const target = this._ambientTarget();
    try { a.volume = 0; } catch (_e) { /* ignore */ }
    const p = a.play();
    if (p && p.catch) p.catch(() => {});
    const steps = 20;
    const stepMs = Math.max(10, ms / steps);
    let i = 0;
    this._ambientFade = setInterval(() => {
      i += 1;
      try { a.volume = Math.min(target, target * (i / steps)); } catch (_e) { /* ignore */ }
      if (i >= steps) this._clearAmbientFade();
    }, stepMs);
  }

  // Stop ALL audio immediately (call when leaving the game).
  stopAll() {
    this._clearAmbientFade();
    for (const k of Object.keys(this.buffers)) {
      const a = this.buffers[k];
      try { a.pause(); a.currentTime = 0; } catch (_e) { /* ignore */ }
    }
    this.ambient = null;
  }
}

export const olAudio = new OutlawsAudio();
